import { ref } from 'vue';
import type { Address } from 'viem';
import { createPublicClient, http, parseUnits } from 'viem';
import { quoterV2Abi } from '../lib/abi';
import { useTokens } from './tokens';
import { useChainStore } from '../stores/chain';

export type SwapCandidate = {
  address: Address;
  symbol: string;
  name: string;
  decimals: number;
  estOut?: string; // raw string returned by quoter for a small input amount
};

function getClient() {
  const chain = useChainStore();
  const cfg = chain.current;
  if (!cfg.rpcUrl) throw new Error('RPC URL not configured for selected chain');
  return createPublicClient({ chain: cfg.chain, transport: http(cfg.rpcUrl) });
}

export function usePools() {
  const { tokens } = useTokens();
  const chain = useChainStore();
  const suggestions = ref<SwapCandidate[]>([]);
  const scanning = ref(false);
  const error = ref<string | null>(null);

  async function suggestFor(fromAddr?: Address, fromDecimals = 18) {
    suggestions.value = [];
    error.value = null;
    const quoter = chain.current.uniswap?.quoter as Address | undefined;
    if (!quoter) return; // no quoter configured on this chain
    if (!fromAddr) return;
    scanning.value = true;
    try {
      const publicClient = getClient();
      // Load union of current tokens and static tokenlist to ensure base assets are present
      const baseSymbols = new Set(['WETH', 'USDC', 'USDT', 'DAI', 'USDC.e']);
      let poolUniverse = tokens.value;
      try {
        const res = await fetch('/tokenlist.json');
        const json = await res.json();
        const list = (json.tokens || []).filter((t: any) => t.chainId === chain.current.id);
        // Deduplicate by address
        const seen = new Set(poolUniverse.map((t) => t.address.toLowerCase()));
        for (const t of list) {
          const key = (t.address as string).toLowerCase();
          if (!seen.has(key)) { (poolUniverse as any).push(t); seen.add(key); }
        }
      } catch {}
      const candidates = poolUniverse
        .filter((t) => t.address !== fromAddr && baseSymbols.has(t.symbol.toUpperCase()))
        .slice(0, 6);
      const res: SwapCandidate[] = [];
      for (const t of candidates) {
        try {
          const amountIn = parseUnits('1', Math.min(fromDecimals, 6)); // small test amount
          const [out] = (await publicClient.readContract({
            address: quoter,
            abi: quoterV2Abi,
            functionName: 'quoteExactInputSingle',
            args: [
              {
                tokenIn: fromAddr,
                tokenOut: t.address,
                fee: Number(chain.current.uniswap?.poolFee || 3000),
                recipient: '0x0000000000000000000000000000000000000000',
                amountIn,
                sqrtPriceLimitX96: 0n
              }
            ]
          })) as any;
          if (out && BigInt(out) > 0n) res.push({ ...t, estOut: out.toString() });
        } catch (_) {
          // ignore pairs without pools/routes
        }
      }
      suggestions.value = res;
    } catch (e: any) {
      error.value = e?.message || 'Failed to scan pools';
    } finally {
      scanning.value = false;
    }
  }

  return { suggestions, scanning, error, suggestFor };
}
